import { useState } from 'react';
import type React from 'react';
import { delimited } from '@/common/utils/commonUtil';

// 入力できる金額はこの値未満
const AMOUNT_LIMIT = 1_000_000_000_000;

const numberWithComma = (value: string): string => {
  if (!/^[0-9]+$/.test(value)) {
    return value;
  }
  return delimited(Math.floor(parseInt(value)));
};

const deleteComma = (value: string): string => value.replace(/,/g, '');

// カンマ付きの入力（1,000,000）も正しい値として扱う
const errorMessageOnChange = (value: string): string | null => {
  const digits = deleteComma(value);
  if (digits === '') {
    return null;
  }
  if (!/^[0-9]+$/.test(digits)) {
    return '半角数字を入力してください';
  }
  if (parseInt(digits) >= AMOUNT_LIMIT) {
    return `${delimited(AMOUNT_LIMIT)}未満の値を入力してください`;
  }
  return null;
};

export interface AmountInputBindings {
  value: string;
  'aria-required': boolean;
  'aria-invalid': boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export interface AmountFieldOptions {
  required: boolean;
}

export interface AmountFieldController {
  required: boolean;
  error: string | null;
  // 計算に使う値。空は 0
  amount: number;
  isRequiredEmpty: boolean;
  fillZeroIfEmpty: () => void;
  showRequiredError: () => void;
  inputProps: AmountInputBindings;
}

// 金額の入力欄 1 つ分の状態。表示中はカンマ区切り、フォーカス中はカンマなしで編集する
export function useAmountField({
  required,
}: AmountFieldOptions): AmountFieldController {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const update = (next: string) => {
    setValue(next);
    setError(errorMessageOnChange(next));
  };

  return {
    required,
    error,
    amount: value === '' ? 0 : parseInt(deleteComma(value)),
    isRequiredEmpty: required && value === '',
    fillZeroIfEmpty: () => {
      if (value === '') update('0');
    },
    showRequiredError: () => setError('入力してください'),
    inputProps: {
      value,
      'aria-required': required,
      'aria-invalid': error != null,
      onChange: e => update(e.target.value),
      onFocus: e => setValue(deleteComma(e.target.value)),
      onBlur: e => setValue(numberWithComma(deleteComma(e.target.value))),
    },
  };
}
